import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Mail, Phone, MapPin, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/utils/api';

export const ContactUs = () => {
  const [form, setForm] = useState({ subject: '', message: '' });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post('/contact/authenticated', form);
      toast.success("Message sent! We'll get back to you soon.");
      setForm({ subject: '', message: '' });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 md:p-8 max-w-5xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="p-2 bg-sage-100 rounded-xl">
            <MessageSquare className="w-6 h-6 text-sage-600" />
          </div>
          <h1 className="text-3xl font-heading font-bold text-sage-800">Contact Us</h1>
        </div>
        <p className="text-gray-500 ml-14">
          Have a question, feedback, or need support? Fill out the form and we'll get back to you.
        </p>
      </div>

      <div className="grid md:grid-cols-5 gap-8 items-start">
        {/* Contact info panel */}
        <div className="md:col-span-2 space-y-6">
          <div className="bg-white rounded-3xl p-6 shadow-sm border border-sage-100 space-y-6">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-sage-100 rounded-2xl shrink-0">
                <Mail className="w-5 h-5 text-sage-600" />
              </div>
              <div>
                <p className="font-heading font-semibold text-sage-800 mb-0.5">Email Support</p>
                <p className="text-sm text-gray-600">support@vetcare.app</p>
                <p className="text-xs text-gray-400 mt-1">Replies within 24 hours</p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="p-3 bg-sage-100 rounded-2xl shrink-0">
                <Phone className="w-5 h-5 text-sage-600" />
              </div>
              <div>
                <p className="font-heading font-semibold text-sage-800 mb-0.5">Phone</p>
                <p className="text-sm text-gray-600">+1 (800) 838-2273</p>
                <p className="text-xs text-gray-400 mt-1">Mon–Fri, 9am–6pm EST</p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="p-3 bg-sage-100 rounded-2xl shrink-0">
                <MapPin className="w-5 h-5 text-sage-600" />
              </div>
              <div>
                <p className="font-heading font-semibold text-sage-800 mb-0.5">Office</p>
                <p className="text-sm text-gray-600">123 Veterinary Lane, Suite 100</p>
                <p className="text-sm text-gray-600">New York, NY 10001</p>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-sage-500 to-sage-700 rounded-3xl p-6 text-white">
            <h3 className="font-heading font-semibold text-lg mb-2">Need Urgent Help?</h3>
            <p className="text-sage-100 text-sm leading-relaxed">
              For critical issues affecting your clinic operations, please call us directly or email with "URGENT" in the subject line.
            </p>
          </div>
        </div>

        {/* Form panel */}
        <form
          onSubmit={handleSubmit}
          className="md:col-span-3 bg-white rounded-3xl p-8 shadow-sm border border-sage-100 space-y-5"
        >
          <h2 className="text-xl font-heading font-semibold text-sage-800">Send Us a Message</h2>

          <div>
            <Label htmlFor="cu-subject">Subject</Label>
            <Input
              id="cu-subject"
              placeholder="How can we help?"
              value={form.subject}
              onChange={(e) => setForm({ ...form, subject: e.target.value })}
              required
              maxLength={200}
              className="mt-1 rounded-xl"
            />
          </div>

          <div>
            <Label htmlFor="cu-message">Message</Label>
            <textarea
              id="cu-message"
              placeholder="Tell us more about your question, issue, or feedback..."
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              required
              maxLength={2000}
              rows={6}
              className="mt-1 w-full rounded-xl border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 resize-none"
            />
            <p className="text-xs text-gray-400 mt-1 text-right">{form.message.length}/2000</p>
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-sage-500 hover:bg-sage-600 text-white rounded-xl py-5 text-base"
          >
            {loading ? 'Sending...' : 'Send Message'}
          </Button>
        </form>
      </div>
    </div>
  );
};
